import { move } from '@dnd-kit/helpers'
import { DragDropProvider, DragEndEvent } from '@dnd-kit/react'
import { isSortable, useSortable } from '@dnd-kit/react/sortable'
import {
  Box,
  BoxProps,
  chakra,
  Checkbox,
  FormControl,
  forwardRef,
  GridItem,
  HStack,
  Icon,
  SimpleGrid,
  Text,
} from '@eigtech/ui-shared-dave'
import { ColumnOrderState } from '@tanstack/react-table'
import { useState } from 'react'
import { MdDragIndicator } from 'react-icons/md'
import { useDataGridContext } from '../DataGridContext'

export const ACTION_MENU_ID = 'actionMenu'

export const DataGridColumnSettings = forwardRef<BoxProps, 'div'>((props, ref) => {
  const { enableReordering, table } = useDataGridContext()

  const [columns, setColumns] = useState(
    table
      .getAllLeafColumns()
      .filter(
        (column) =>
          (column.getCanHide() ||
            (enableReordering && column.columnDef.meta?.enableReordering !== false)) &&
          column.id !== ACTION_MENU_ID
      )
      .map((column) => column.id)
  )

  function handleDragEnd(event: DragEndEvent) {
    if (event.canceled) return

    const { source } = event.operation

    if (!isSortable(source)) return

    const { initialIndex, index } = source

    if (initialIndex === index) return

    const sourceId = columns[initialIndex]
    const targetId = columns[index]

    if (!sourceId || !targetId) return

    const { columnOrder } = table.getState()

    const newColumnOrder = reorderColumn(sourceId.toString(), targetId.toString(), columnOrder)
    table.setColumnOrder(newColumnOrder)

    setColumns((columns) => move(columns, event))
  }

  return (
    <Box ref={ref} {...props}>
      <SimpleGrid columnGap={4} columns={2} rowGap={2}>
        <GridItem colSpan={2}>
          <Text color="blackAlpha.600" fontSize="xs" fontWeight="bold">
            Column settings:
          </Text>
        </GridItem>

        <DragDropProvider onDragEnd={handleDragEnd}>
          {columns.map((columnId, index) => (
            <ColumnSettings key={columnId} columnId={columnId} index={index} />
          ))}
        </DragDropProvider>
      </SimpleGrid>
    </Box>
  )
})

type ColumnSettingsProps = {
  columnId: string
  index: number
}

function ColumnSettings({ columnId, index }: ColumnSettingsProps) {
  const { enableReordering, table } = useDataGridContext()

  const { isDragging, ref, handleRef } = useSortable({ id: columnId, index })

  const column = table.getColumn(columnId)

  if (!column) return null

  const label = (
    <chakra.span fontSize="sm" fontWeight="semibold">
      {column.columnDef.meta?.headerLabel ??
        (typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id)}
    </chakra.span>
  )

  return (
    <GridItem ref={ref}>
      <HStack justifyContent="space-between" opacity={isDragging ? 0.5 : 1} px="0.5" spacing="1">
        {column.getCanHide() ? (
          <FormControl>
            <Checkbox
              isChecked={column.getIsVisible()}
              onChange={column.getToggleVisibilityHandler()}
            >
              {label}
            </Checkbox>
          </FormControl>
        ) : (
          label
        )}

        {enableReordering && column.columnDef.meta?.enableReordering !== false && (
          <chakra.button
            ref={handleRef}
            alignItems="center"
            display="inline-flex"
            justifyContent="center"
          >
            <Icon as={MdDragIndicator} cursor={isDragging ? 'grabbing' : 'grab'} />
          </chakra.button>
        )}
      </HStack>
    </GridItem>
  )
}

const reorderColumn = (
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: string[]
): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0] as string
  )
  return [...columnOrder]
}
